import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, Phone, Mail, Star, TrendingUp, MessageCircle, FileText, ChevronRight, RefreshCw, Send, Receipt, Pen, Camera, Download, ExternalLink, ZoomIn, UserPlus, KeyRound } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import ClientCreateModal from './ClientCreateModal';
import DevisCreateModal from './DevisCreateModal';
import { LocationCard } from './clients/LocationCard';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN   = () => sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
const H       = () => ({ Authorization: `Bearer ${TOKEN()}` });

const DOC_TYPE_CFG = {
  contrat:   { label: 'Contrat',   icon: <FileText size={12}/>,  color: 'var(--admin-teal)',   bg: 'var(--admin-teal-dim)' },
  facture:   { label: 'Facture',   icon: <Receipt size={12}/>,  color: 'var(--admin-gold)',   bg: 'var(--admin-gold-dim)' },
  signature: { label: 'Signature', icon: <Pen size={12}/>,      color: 'var(--admin-green)',  bg: 'var(--admin-green-dim)' },
  photo:     { label: 'Photo',     icon: <Camera size={12}/>,   color: 'var(--admin-blue)',   bg: 'var(--admin-blue-dim)' },
};

// ── Mini composant documents de la fiche client ────────────────────────────
function ClientDocumentsTab({ client, loading: parentLoading }) {
  const [docs,    setDocs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState({});

  useEffect(() => {
    if (!client?.nom && !client?.email) return;
    setLoading(true);
    const search = encodeURIComponent(client.nom || client.email || '');
    fetch(`${BACKEND}/api/admin/documents?client_nom=${search}&limit=30`, { headers: H() })
      .then(r => r.json())
      .then(d => { setDocs(d.documents || []); setTotal(d.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  async function renvoyer(doc) {
    if (!doc.can_resend) return;
    setSending(p => ({ ...p, [doc.id]: true }));
    try {
      const res = await fetch(`${BACKEND}/api/admin/documents/${doc.quote_id}/renvoyer/${doc.type}`,
        { method: 'POST', headers: H() });
      const d = await res.json();
      toast.success(d.success ? `${DOC_TYPE_CFG[doc.type]?.label} renvoyé !` : d.message || 'Erreur');
    } catch { toast.error('Erreur'); }
    setSending(p => ({ ...p, [doc.id]: false }));
  }

  if (loading || parentLoading) return (
    <div style={{ textAlign: 'center', padding: 30, color: 'var(--admin-faint)' }}>Chargement...</div>
  );
  if (docs.length === 0) return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--admin-faint)' }}>
      <FileText size={32} style={{ margin: '0 auto 10px', opacity: .2 }}/>
      <p style={{ fontSize: 14 }}>Aucun document pour ce client</p>
      <p style={{ fontSize: 12, marginTop: 6 }}>Contrats et factures apparaîtront ici dès qu&apos;une commande sera clôturée.</p>
    </div>
  );

  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--admin-muted)', marginBottom: 12 }}>
        {total} document{total > 1 ? 's' : ''} rattaché{total > 1 ? 's' : ''} à ce client
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {docs.map(doc => {
          const cfg = DOC_TYPE_CFG[doc.type] || DOC_TYPE_CFG.contrat;
          return (
            <div key={doc.id} className="adm-card" style={{ padding: '11px 13px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center', background: cfg.bg, color: cfg.color }}>
                {cfg.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 6px', borderRadius: 6,
                    background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                  {doc.numero && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-gold)',
                      fontFamily: 'var(--admin-font-display)' }}>{doc.numero}</span>
                  )}
                </div>
                <p style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{formatDate(doc.date)} · {doc.statut}</p>
              </div>
              <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                {(doc.url_view || doc.signature_data) && (
                  <button onClick={() => setPreview(doc)} title="Aperçu"
                    style={{ width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: 'var(--admin-surface2)', color: 'var(--admin-muted)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ZoomIn size={12}/>
                  </button>
                )}
                {doc.url_dl && (
                  <a href={`${BACKEND}${doc.url_dl}`} target="_blank" rel="noreferrer" title="Télécharger"
                    style={{ width: 30, height: 30, borderRadius: 8,
                      background: 'var(--admin-blue-dim)', color: 'var(--admin-blue)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                    <ExternalLink size={12}/>
                  </a>
                )}
                {doc.can_resend && (
                  <button onClick={() => renvoyer(doc)} disabled={sending[doc.id]} title="Renvoyer"
                    style={{ width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: sending[doc.id] ? .6 : 1 }}>
                    <Send size={12}/>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Aperçu inline */}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(4,6,9,.9)',
          backdropFilter: 'blur(6px)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 18px', background: 'var(--admin-bg2)', borderBottom: '1px solid var(--admin-border-soft)' }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--admin-text)' }}>
              {DOC_TYPE_CFG[preview.type]?.label} — {preview.numero}
            </p>
            <button onClick={() => setPreview(null)}
              style={{ padding: '6px 11px', borderRadius: 9, background: 'var(--admin-surface)',
                border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
              Fermer
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            {(preview.type === 'contrat' || preview.type === 'facture') && preview.url_view && (
              <iframe src={`${BACKEND}${preview.url_view}`}
                style={{ width: '100%', height: '100%', minHeight: 600, border: 'none', borderRadius: 12, background: '#fff' }}
                title="Aperçu document" />
            )}
            {preview.type === 'signature' && preview.signature_data && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <img src={preview.signature_data} alt="Signature"
                  style={{ maxWidth: 400, border: '1px solid var(--admin-border)', borderRadius: 12, background: '#fff', padding: 16 }} />
              </div>
            )}
            {preview.type === 'photo' && preview.url_view && (
              <div style={{ textAlign: 'center' }}>
                <img src={preview.url_view} alt="Photo" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 12 }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Étiquettes de canal ───────────────────────────────────────────────────
const CANAL_STYLE = {
  formulaire: { label: 'Formulaire du site', bg: 'var(--admin-blue-dim)',   color: 'var(--admin-blue)' },
  chat:       { label: 'Chat du site',       bg: 'var(--admin-purple-dim)', color: 'var(--admin-purple)' },
  email:      { label: 'Email',              bg: 'var(--admin-gold-dim)',   color: 'var(--admin-gold)' },
};

// ─── Composant fil unifié ─────────────────────────────────────────────────
function UnifiedMessageThread({ client, messages, hasChat, threadIds, summary, loading, onReload }) {
  const [replyText, setReplyText]     = useState('');
  const [activeTid, setActiveTid]     = useState(threadIds[0] || null);
  const [sending,   setSending]       = useState(false);
  const [replyMode, setReplyMode]     = useState('chat'); // 'chat' ou 'email'
  const [emailSubject, setEmailSubject] = useState('');
  
  // Déterminer si on peut répondre par email (au moins 1 message email entrant)
  const hasInboundEmail = messages.some(m => m.canal === 'email' && m.direction === 'entrant');

  async function sendReply() {
    if (!replyText.trim()) return;
    
    // Si mode email et pas de thread chat, forcer email
    const finalMode = (!hasChat || replyMode === 'email') ? 'email' : 'chat';
    
    if (finalMode === 'chat' && !activeTid) {
      toast.error('Aucun thread chat actif');
      return;
    }
    
    setSending(true);
    try {
      const endpoint = finalMode === 'email' 
        ? `${BACKEND}/api/admin/clients/${client.id}/messages-unified/reply-email`
        : `${BACKEND}/api/admin/clients/${client.id}/messages-unified/reply-chat`;
      
      const payload = finalMode === 'email'
        ? { to_email: client.email, subject: emailSubject || 'Re: Votre demande', body: replyText.trim() }
        : { thread_id: activeTid, body: replyText.trim() };
      
      const res = await fetch(endpoint, { 
        method: 'POST', 
        headers: { ...H(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload) 
      });
      const d = await res.json();
      
      if (d.success) {
        toast.success(finalMode === 'email' ? 'Email envoyé ✉️' : 'Réponse envoyée dans le chat');
        setReplyText('');
        setEmailSubject('');
        if (onReload) onReload();
      } else { 
        toast.error(d.message || 'Erreur'); 
      }
    } catch { 
      toast.error('Erreur envoi'); 
    }
    setSending(false);
  }

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 30, color: 'var(--admin-faint)' }}>Chargement...</div>
  );

  if (messages.length === 0) return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--admin-faint)' }}>
      <MessageCircle size={32} style={{ margin: '0 auto 10px', opacity: .2 }}/>
      <p>Aucun message</p>
      <p style={{ fontSize: 12, marginTop: 6 }}>
        Les messages du formulaire et du chat apparaîtront ici.
      </p>
    </div>
  );

  return (
    <div>
      {/* Résumé canaux */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {Object.entries(CANAL_STYLE).map(([k, v]) => (
          <span key={k} style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 8,
            background: v.bg, color: v.color }}>
            {v.label} ({summary[k] || 0})
          </span>
        ))}
      </div>

      {/* Fil chronologique */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {messages.map((msg, i) => {
          const canal   = CANAL_STYLE[msg.canal] || CANAL_STYLE.formulaire;
          const isAdmin = msg.sender === 'admin';
          return (
            <div key={i} style={{
              alignSelf: isAdmin ? 'flex-end' : 'flex-start',
              maxWidth: '86%', display: 'flex', flexDirection: 'column',
              alignItems: isAdmin ? 'flex-end' : 'flex-start',
            }}>
              {/* En-tête : canal + date */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 6,
                  background: canal.bg, color: canal.color }}>
                  {canal.label}
                </span>
                <span style={{ fontSize: 10, color: 'var(--admin-faint)' }}>{formatDate(msg.created_at)}</span>
              </div>
              {/* Bulle */}
              <div style={{
                padding: '10px 13px', borderRadius: 13, fontSize: 13.5, lineHeight: 1.5,
                background: isAdmin ? 'var(--admin-teal-dim)' : 'var(--admin-surface)',
                border: `1px solid ${isAdmin ? 'var(--admin-teal-line)' : 'var(--admin-border-soft)'}`,
                borderBottomRightRadius: isAdmin ? 4 : 13,
                borderBottomLeftRadius:  isAdmin ? 13 : 4,
                color: 'var(--admin-text)',
              }}>
                {msg.sujet && (
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-gold)', marginBottom: 4 }}>
                    Sujet : {msg.sujet}
                  </p>
                )}
                {msg.body || '(message vide)'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Zone de réponse */}
      <div className="adm-card" style={{ padding: '13px 14px' }}>
        {hasChat || hasInboundEmail ? (
          <>
            {/* Toggle Chat/Email si les deux sont disponibles */}
            {hasChat && hasInboundEmail && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <button onClick={() => setReplyMode('chat')}
                  style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: replyMode === 'chat' ? 'var(--admin-purple-dim)' : 'transparent',
                    color: replyMode === 'chat' ? 'var(--admin-purple)' : 'var(--admin-faint)',
                    border: `1px solid ${replyMode === 'chat' ? 'var(--admin-purple)' : 'var(--admin-border)'}`,
                    cursor: 'pointer' }}>
                  💬 Chat
                </button>
                <button onClick={() => setReplyMode('email')}
                  style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: replyMode === 'email' ? 'var(--admin-gold-dim)' : 'transparent',
                    color: replyMode === 'email' ? 'var(--admin-gold)' : 'var(--admin-faint)',
                    border: `1px solid ${replyMode === 'email' ? 'var(--admin-gold)' : 'var(--admin-border)'}`,
                    cursor: 'pointer' }}>
                  ✉️ Email
                </button>
              </div>
            )}
            
            {/* Titre */}
            <p style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--admin-muted)', marginBottom: 8 }}>
              {(hasChat && replyMode === 'chat') ? 'Répondre au chat' : 'Répondre par email'}
            </p>
            
            {/* Champ sujet (email uniquement) */}
            {(!hasChat || replyMode === 'email') && (
              <input
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                placeholder="Sujet de l'email (ex: Re: Votre demande)"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 9, fontSize: 13,
                  background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)',
                  color: 'var(--admin-text)', outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}
              />
            )}
            
            {/* Zone de texte */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder={replyMode === 'email' ? 'Votre message email...' : 'Votre réponse...'}
                rows={replyMode === 'email' ? 4 : 2}
                style={{ flex: 1, resize: 'none', padding: '9px 12px', borderRadius: 10, fontSize: 13.5,
                  background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)',
                  color: 'var(--admin-text)', outline: 'none', fontFamily: 'inherit' }}
              />
              <button onClick={sendReply} disabled={sending || !replyText.trim()}
                style={{ padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                  background: replyText.trim() ? (replyMode === 'email' ? 'var(--admin-gold)' : 'var(--admin-teal)') : 'var(--admin-surface2)',
                  color: replyText.trim() ? '#052b25' : 'var(--admin-faint)',
                  border: 'none', cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                  opacity: sending ? .6 : 1,
                  display: 'flex', alignItems: 'center', gap: 6 }}>
                <Send size={13}/> {sending ? '...' : 'Envoyer'}
              </button>
            </div>
            
            {threadIds.length > 1 && replyMode === 'chat' && (
              <p style={{ fontSize: 11, color: 'var(--admin-faint)', marginTop: 8 }}>
                {threadIds.length} conversations actives
              </p>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <p style={{ fontSize: 12.5, color: 'var(--admin-muted)', marginBottom: 4 }}>
              💬 Aucune conversation active
            </p>
            <p style={{ fontSize: 11.5, color: 'var(--admin-faint)' }}>
              Les messages apparaîtront ici une fois que le client vous contacte.
              {client.email && (
                <> <a href={`mailto:${client.email}`} style={{ color: 'var(--admin-teal)' }}>
                  Ouvrir dans votre client email →
                </a></>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const PROFILE_COLORS = {
  particulier:  { color: 'var(--admin-muted)',  bg: 'var(--admin-surface2)', label: 'Particulier' },
  entreprise:   { color: 'var(--admin-blue)',   bg: 'var(--admin-blue-dim)', label: 'Entreprise' },
  association:  { color: 'var(--admin-purple)', bg: 'var(--admin-purple-dim)', label: 'Association' },
  ce:           { color: 'var(--admin-gold)',   bg: 'var(--admin-gold-dim)', label: 'CE' },
  groupe:       { color: 'var(--admin-green)',  bg: 'var(--admin-green-dim)', label: 'Groupe' },
};

export default function AdminClients() {
  const [clients,      setClients]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientData,   setClientData]   = useState(null); // devis + messages + fidélité
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab,    setActiveTab]    = useState('locations');
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [createDevisFor,   setCreateDevisFor]   = useState(null);
  const [backfilling,      setBackfilling]      = useState(false);

  function load() {
    setLoading(true);
    fetch(`${BACKEND}/api/clients`)
      .then(r => r.json())
      .then(d => setClients(d.clients || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  // Force un compte connectable pour TOUS les clients (y compris les anciens)
  async function backfillComptes() {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${TOKEN()}` };
      const dry = await (await fetch(`${BACKEND}/api/clients/backfill-comptes`,
        { method: 'POST', headers: H, body: JSON.stringify({ dry_run: true, send_emails: false }) })).json();
      const aTraiter = (dry.crees || 0) + (dry.mots_de_passe_poses || 0);
      if (aTraiter === 0) { toast.info('Tous les clients ont déjà un compte connectable ✓'); setBackfilling(false); return; }
      const ok = window.confirm(
        `Créer les comptes manquants\n\n` +
        `• ${dry.crees} compte(s) à créer (clients sans compte)\n` +
        `• ${dry.mots_de_passe_poses} mot(s) de passe temporaire(s) à poser (anciens contacts)\n` +
        `• ${dry.ignores_existants} client(s) déjà connectable(s) · ${dry.ignores_test} de test ignoré(s)\n\n` +
        `Un email de bienvenue (identifiants + invitation à changer le mot de passe) sera envoyé à chaque client concerné.\n\nConfirmer ?`
      );
      if (!ok) { setBackfilling(false); return; }
      const res = await (await fetch(`${BACKEND}/api/clients/backfill-comptes`,
        { method: 'POST', headers: H, body: JSON.stringify({ dry_run: false, send_emails: true }) })).json();
      toast.success('Comptes clients traités ✓', {
        description: `${res.crees} créé(s) · ${res.mots_de_passe_poses} crédité(s) · ${res.emails_en_file} email(s) en cours d'envoi`,
      });
      load();
    } catch { toast.error('Erreur lors de la création des comptes'); }
    setBackfilling(false);
  }

  async function validateProfile(id, profile) {
    await fetch(`${BACKEND}/api/clients/${id}/validate?profile=${profile}`, { method: 'PUT' });
    toast.success('Profil validé');
    setClients(prev => prev.map(c => c.id === id ? { ...c, profile, valide: true } : c));
  }

  async function openClient(client) {
    setSelectedClient(client);
    setActiveTab('locations');
    setLoadingDetail(true);
    try {
      const [dRes, fRes, msgRes] = await Promise.all([
        fetch(`${BACKEND}/api/devis?client_id=${encodeURIComponent(client.id || '')}&client_email=${encodeURIComponent(client.email || '')}&limit=200`, { headers: H() }).then(r => r.json()),
        fetch(`${BACKEND}/api/fidelite/client/${client.id || client.email}`, { headers: H() }).then(r => r.json()).catch(() => null),
        // Nouveau : messages unifiés (formulaire + chat)
        fetch(`${BACKEND}/api/admin/clients/${client.id}/messages-unified?client_email=${encodeURIComponent(client.email || '')}&limit=60`, { headers: H() }).then(r => r.json()).catch(() => ({ messages: [], total: 0, has_chat: false, thread_ids: [], summary: {} })),
      ]);
      setClientData({
        quotes:          dRes.quotes || [],
        messages:        msgRes.messages || [],       // fil unifié
        messagesTotal:   msgRes.total || 0,
        hasChat:         msgRes.has_chat || false,
        threadIds:       msgRes.thread_ids || [],
        msgSummary:      msgRes.summary || {},
        fidelite:        fRes,
      });
    } catch { setClientData({ quotes: [], messages: [], messagesTotal: 0, hasChat: false, threadIds: [], msgSummary: {}, fidelite: null }); }
    setLoadingDetail(false);
  }

  const filtered = search
    ? clients.filter(c => [c.nom, c.prenom, c.email].some(v => v?.toLowerCase().includes(search.toLowerCase())))
    : clients;

  // ── Fiche client 360° ──────────────────────────────────────────────────────
  if (selectedClient) {
    const c   = selectedClient;
    const d   = clientData;
    const fid = d?.fidelite;
    const pc  = PROFILE_COLORS[c.profile] || PROFILE_COLORS.particulier;
    const initials = `${(c.prenom||'?')[0]}${(c.nom||'?')[0]}`.toUpperCase();
    const PAID_ST = ['acompte_paye', 'solde_paye', 'commande', 'valide_admin', 'termine'];
    const caTotal = (d?.quotes || []).filter(q => PAID_ST.includes(q.statut)).reduce((acc, q) => acc + parseFloat(q.total || 0), 0);

    return (
      <div data-testid="admin-client-detail">
        {createDevisFor && (
          <DevisCreateModal
            client={createDevisFor}
            onClose={() => setCreateDevisFor(null)}
            onCreated={() => { setCreateDevisFor(null); openClient(c); }}
          />
        )}
        {/* Retour */}
        <button onClick={() => { setSelectedClient(null); setClientData(null); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-muted)',
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14, padding: '4px 0' }}>
          ← Tous les clients
        </button>

        {/* Carte client */}
        <div className="adm-card" style={{ padding: 18, display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--admin-font-display)',
            fontWeight: 700, fontSize: 18, background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 20, fontWeight: 700,
              letterSpacing: '-.3px', color: 'var(--admin-text)', marginBottom: 4 }}>
              {c.prenom} {c.nom}
            </h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              {c.email && <span style={{ fontSize: 12.5, color: 'var(--admin-muted)', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11}/>{c.email}</span>}
              {c.telephone && <span style={{ fontSize: 12.5, color: 'var(--admin-muted)', display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={11}/>{c.telephone}</span>}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 8,
                background: pc.bg, color: pc.color }}>{pc.label}</span>
              {c.valide && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 8,
                background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' }}>✓ Validé</span>}
            </div>
            {/* Stats rapides */}
            <div style={{ display: 'flex', gap: 20, marginTop: 14 }}>
              <div><p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 18, fontWeight: 700,
                color: 'var(--admin-text)', display: 'block' }}>{(d?.quotes || []).length}</p>
                <small style={{ fontSize: 11, color: 'var(--admin-faint)' }}>locations</small></div>
              <div><p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 18, fontWeight: 700,
                color: 'var(--admin-text)', display: 'block' }}>{caTotal.toFixed(0)}€</p>
                <small style={{ fontSize: 11, color: 'var(--admin-faint)' }}>total dépensé</small></div>
              {fid && <div><p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 18, fontWeight: 700,
                color: 'var(--admin-gold)', display: 'block' }}>{fid.cashback?.toFixed(2) || '0'}€</p>
                <small style={{ fontSize: 11, color: 'var(--admin-faint)' }}>cashback</small></div>}
            </div>
          </div>
        </div>

        {/* Bloc fidélité */}
        {fid && (
          <div style={{ background: `linear-gradient(160deg,var(--admin-gold-dim),var(--admin-surface) 70%)`,
            border: '1px solid rgba(224,178,76,.3)', borderRadius: 'var(--admin-radius)',
            padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Star size={14} style={{ color: 'var(--admin-gold)' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)' }}>Fidélité & Cashback</span>
              {fid.palier && <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700,
                color: 'var(--admin-gold)', background: 'var(--admin-gold-dim)',
                padding: '2px 9px', borderRadius: 20 }}>{fid.palier}</span>}
            </div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 10 }}>
              <div><span style={{ fontFamily: 'var(--admin-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--admin-text)', display: 'block' }}>{fid.points || 0}</span><small style={{ fontSize: 11, color: 'var(--admin-muted)' }}>points</small></div>
              <div><span style={{ fontFamily: 'var(--admin-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--admin-text)', display: 'block' }}>{(fid.cashback||0).toFixed(2)}€</span><small style={{ fontSize: 11, color: 'var(--admin-muted)' }}>cashback dispo</small></div>
            </div>
          </div>
        )}

        {/* Actions rapides */}
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
          {c.email && (
            <a href={`mailto:${c.email}`}
              className="adm-btn adm-btn-pri" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={13}/> Envoyer un mail
            </a>
          )}
          {c.telephone && (
            <a href={`tel:${c.telephone}`} className="adm-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
              <Phone size={13}/> Appeler
            </a>
          )}
          <button className="adm-btn"
            onClick={() => setCreateDevisFor(c)}
            data-testid="client-nouveau-devis-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={13}/> Nouveau devis
          </button>
        </div>

        {/* Onglets — compteur messages inclut les deux canaux */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--admin-border-soft)', marginBottom: 14 }}>
          {[
            { key: 'locations', label: `Locations (${(d?.quotes||[]).length})` },
            { key: 'messages',  label: `Messages (${d?.messagesTotal || 0})` },
            { key: 'docs',      label: 'Documents' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{
                fontSize: 13.5, fontWeight: 600, padding: '10px 14px', cursor: 'pointer',
                background: 'none', border: 'none',
                color: activeTab === tab.key ? 'var(--admin-text)' : 'var(--admin-muted)',
                borderBottom: activeTab === tab.key ? '2px solid var(--admin-teal)' : '2px solid transparent',
                marginBottom: -1,
              }}>{tab.label}</button>
          ))}
        </div>

        {/* Onglet Locations */}
        {activeTab === 'locations' && (
          <div>
            {loadingDetail ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--admin-faint)' }}>Chargement...</div>
            ) : (d?.quotes||[]).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--admin-faint)' }}>
                <FileText size={32} style={{ margin: '0 auto 10px', opacity: .2 }}/>
                <p>Aucune location enregistrée</p>
              </div>
            ) : (d.quotes.map(q => (
              <LocationCard key={q.id} quote={q} />
            )))}
          </div>
        )}

        {/* ── Onglet Messages — fil unifié (Formulaire + Chat) ── */}
        {activeTab === 'messages' && (
          <UnifiedMessageThread
            client={c}
            messages={d?.messages || []}
            hasChat={d?.hasChat || false}
            threadIds={d?.threadIds || []}
            summary={d?.msgSummary || {}}
            loading={loadingDetail}
            onReload={() => openClient(c)}
          />
        )}

        {/* Onglet Documents — filtré par client via l'endpoint unifié */}
        {activeTab === 'docs' && (
          <ClientDocumentsTab client={c} loading={loadingDetail} />
        )}

        {/* Validation profil */}
        {!c.valide && (
          <div className="adm-card" style={{ padding: '14px 16px', marginTop: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--admin-muted)', marginBottom: 10 }}>VALIDER LE PROFIL TARIFAIRE</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['ce','groupe','association','entreprise','particulier'].map(p => (
                <button key={p} onClick={() => validateProfile(c.id, p)}
                  style={{ padding: '7px 13px', borderRadius: 9, fontSize: 12, fontWeight: 600,
                    background: 'var(--admin-gold-dim)', color: 'var(--admin-gold)',
                    border: '1px solid rgba(224,178,76,.3)', cursor: 'pointer' }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Liste clients ─────────────────────────────────────────────────────────
  return (
    <div data-testid="admin-clients">
      {showCreateClient && (
        <ClientCreateModal
          onClose={() => setShowCreateClient(false)}
          onCreated={(nc) => { load(); }}
        />
      )}
      {createDevisFor && (
        <DevisCreateModal
          client={createDevisFor}
          onClose={() => setCreateDevisFor(null)}
          onCreated={() => setCreateDevisFor(null)}
        />
      )}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
          <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
            letterSpacing: '-.6px', color: 'var(--admin-text)' }}>Base clients</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowCreateClient(true)} data-testid="nouveau-client-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
                border: 'none', background: 'var(--admin-teal)', color: '#052b25',
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              <UserPlus size={13}/> Nouveau client
            </button>
            <button onClick={backfillComptes} disabled={backfilling} data-testid="backfill-comptes-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
                border: 'none', background: 'var(--admin-gold, #C58B19)', color: '#1a1205',
                fontSize: 12.5, fontWeight: 700, cursor: backfilling ? 'wait' : 'pointer', opacity: backfilling ? 0.7 : 1 }}>
              <KeyRound size={13}/> {backfilling ? 'Traitement…' : 'Créer les comptes manquants'}
            </button>
            <button onClick={load}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 10,
                border: '1px solid var(--admin-border)', background: 'var(--admin-surface)',
                color: 'var(--admin-muted)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              <RefreshCw size={13}/> Actualiser
            </button>
          </div>
        </div>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
          {clients.length} client{clients.length > 1 ? 's' : ''} enregistré{clients.length > 1 ? 's' : ''}
        </p>
      </div>

      {/* Recherche */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Users size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--admin-faint)' }} />
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par nom, email..."
          style={{ width: '100%', padding: '9px 14px 9px 34px', borderRadius: 10,
            background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)',
            color: 'var(--admin-text)', fontSize: 13.5, outline: 'none', boxSizing: 'border-box' }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-faint)' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--admin-faint)' }}>
          <Users size={36} style={{ margin: '0 auto 12px', opacity: .2 }}/>
          <p>Aucun client trouvé</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filtered.map(c => {
            const pc = PROFILE_COLORS[c.profile] || PROFILE_COLORS.particulier;
            const initials = `${(c.prenom||'?')[0]}${(c.nom||'?')[0]}`.toUpperCase();
            return (
              <div key={c.id} className="adm-card"
                style={{ padding: '12px 15px', cursor: 'pointer', transition: 'border-color .15s, transform .12s',
                  display: 'flex', alignItems: 'center', gap: 12 }}
                onClick={() => openClient(c)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--admin-teal-line)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--admin-border-soft)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 11, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--admin-font-display)', fontWeight: 700, fontSize: 13,
                  background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 2 }}>
                    {c.prenom} {c.nom}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{c.email}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 7,
                    background: pc.bg, color: pc.color }}>{pc.label}</span>
                  {c.valide && <span style={{ fontSize: 11, color: 'var(--admin-green)' }}>✓</span>}
                  <ChevronRight size={14} style={{ color: 'var(--admin-faint)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
